import type { UseQueryOptions } from "@tanstack/react-query";
import dayjs from "dayjs";

import Sentry from "@similarweb/sentry-logger";

import { DefaultFetchService, NoCacheHeaders } from "app/services/fetchService";
import queryClient from "single-spa/react-app/query-client";

import type { ExplicitLLModel, IAiTrackerCampaign, IAiTrackerCampaignDetails, ICampaignModalData } from "../types";
import { CampaignStatus, LLModel } from "../types";

const fetchService = DefaultFetchService.getInstance();
const BASE_URL = "/api/aiTracker";
const GET_CAMPAIGNS_ENDPOINT = `${BASE_URL}/getCampaigns`;
const CUD_CAMPAIGN_ENDPOINT = `${BASE_URL}/campaign`;
const CUD_CAMPAIGN_V2_ENDPOINT = `${CUD_CAMPAIGN_ENDPOINT}/v2`;
const CUD_CAMPAIGN_V4_ASYNC_ENDPOINT = `${CUD_CAMPAIGN_ENDPOINT}/v4/create-async`;
const CUD_CAMPAIGN_V4_SYNC_ENDPOINT = `${CUD_CAMPAIGN_ENDPOINT}/v4/create-sync`;

export const LLM_TRACKER_CAMPAIGNS_QUERY_KEY = "llm-tracker-campaigns";

const TEN_MINUTES = 10 * 60 * 1000; // 10 minutes

interface IAiTrackerCampaignsResponse {
    userCampaigns: IAiTrackerCampaign[];
    sharedCampaigns: IAiTrackerCampaign[];
    demoCampaigns: IAiTrackerCampaign[];
}

export interface ICreateCampaignBasePayload {
    campaignName: string;
    domain?: string;
    primaryDomain?: string;
    additionalDomains?: string[];
    country: number;
    brands: string[];
    cultureName: string;
    competitorBrands: IUpdateCampaignCompetitor[];
    includeSubdomains: boolean;
}

export interface ICreateCampaignChatbot {
    name: ExplicitLLModel;
    numOfPrompts?: number;
}

export interface ICreateCampaignPayload extends ICreateCampaignBasePayload {
    chatbots: ICreateCampaignChatbot[];
    topics: string[];
    promptsPerTopic: number;
}

export interface ICreateCampaignPrompt {
    query: string;
    topicName: string;
    intent?: string;
    llms: ExplicitLLModel[];
}

export interface ICreateCampaignWithPromptsPayload extends ICreateCampaignBasePayload {
    chatbots: ExplicitLLModel[];
    prompts: ICreateCampaignPrompt[];
}

interface IUpdateCampaignPrompt {
    promptId: string;
    query: string;
    intent?: string;
    llms: ExplicitLLModel[];
}

interface IUpdateCampaignTopic {
    topicId: string;
    topicName: string;
    prompts: IUpdateCampaignPrompt[];
}

export interface IUpdateCampaignCompetitor {
    domain: string;
    brands: string[];
}

interface IUpdateCampaignPayload {
    campaignId: string;
    lastModifiedDate: string;
    ownerId?: number;
    name?: string;
    brands?: string[];
    domains?: string[];
    includeSubdomains?: boolean;
    chatbots?: ExplicitLLModel[];
    competitorBrands?: IUpdateCampaignCompetitor[];
    topics?: IUpdateCampaignTopic[];
}

export interface IAiTrackerCreateCampaignResponse {
    campaignId: string;
}

interface ICampaignDetailsPayload {
    campaignId: string;
}

export const campaignKeys = {
    getAll: [LLM_TRACKER_CAMPAIGNS_QUERY_KEY],
    getById: (id?: string) => [...campaignKeys.getAll, "byId", id] as const,
} as const;

const createCampaign = async (payload: ICreateCampaignPayload): Promise<IAiTrackerCreateCampaignResponse> => {
    return await fetchService.post(CUD_CAMPAIGN_V4_ASYNC_ENDPOINT, payload);
};

const createCampaignWithPrompts = async (
    payload: ICreateCampaignWithPromptsPayload,
): Promise<IAiTrackerCreateCampaignResponse> => {
    return await fetchService.post(CUD_CAMPAIGN_V4_SYNC_ENDPOINT, payload);
};

const updateCampaign = async (payload: IUpdateCampaignPayload): Promise<void> => {
    await fetchService.put(`${CUD_CAMPAIGN_V2_ENDPOINT}/${payload.campaignId}`, payload);
};

const getDetails = async (payload: ICampaignDetailsPayload): Promise<IAiTrackerCampaignDetails> => {
    return await fetchService.get(`${CUD_CAMPAIGN_ENDPOINT}/${payload.campaignId}/fullDetails`, undefined, {
        headers: NoCacheHeaders,
    });
};

const deleteCampaign = async (campaign: ICampaignModalData): Promise<void> => {
    await fetchService.delete(`${CUD_CAMPAIGN_ENDPOINT}/${campaign.id}`);
};

const WEEKLY_REPORT_ENDPOINT = `${BASE_URL}/notifications/weeklyReport`;

const subscribeWeeklyReport = async (campaignId: string, subscribe: boolean): Promise<void> => {
    const action = subscribe ? "subscribe" : "unsubscribe";
    await fetchService.post(`${WEEKLY_REPORT_ENDPOINT}/${campaignId}/${action}`, {});
};

/**
 * If a configurations record contains more than one item - insert a new aggregated configuration to it.
 */
export const withAllModelsConfig = (campaign: IAiTrackerCampaign): IAiTrackerCampaign => {
    const modelConfigs = Object.entries(campaign.configs ?? {}).filter(([key]) => (key as LLModel) !== LLModel.All);

    if (modelConfigs.length < 2) {
        return campaign;
    }

    //earliest firstScrapeDate within all configurations (ignoring undefined)
    const firstScrapeDate = modelConfigs
        .filter(([, config]) => config.lastScrapeDate)
        .reduce<string | undefined>((min, [, config]) => {
            if (!config.firstScrapeDate) {
                return min;
            }
            if (!min) {
                return config.firstScrapeDate;
            }
            return dayjs(config.firstScrapeDate).isBefore(dayjs(min)) ? config.firstScrapeDate : min;
        }, undefined);

    //maximal lastScrapeDate within all configurations (ignoring undefined)
    const lastScrapeDate = modelConfigs
        .filter(([, config]) => config.firstScrapeDate)
        .reduce<string | undefined>((max, [, config]) => {
            if (!config.lastScrapeDate) {
                return max;
            }
            if (!max) {
                return config.lastScrapeDate;
            }
            return dayjs(config.lastScrapeDate).isAfter(dayjs(max)) ? config.lastScrapeDate : max;
        }, undefined);

    return {
        ...campaign,
        configs: {
            [LLModel.All]: {
                configId: modelConfigs.map(([, config]) => config.configId).join(","),
                firstScrapeDate,
                lastScrapeDate,
                status: firstScrapeDate && lastScrapeDate ? CampaignStatus.Completed : CampaignStatus.Pending,
            },
            ...campaign.configs,
        },
    };
};

const fetchCampaigns = async (): Promise<Array<IAiTrackerCampaign>> => {
    try {
        const { userCampaigns, sharedCampaigns, demoCampaigns }: IAiTrackerCampaignsResponse = await fetchService.get(
            GET_CAMPAIGNS_ENDPOINT,
            undefined,
            {
                preventAutoCancellation: true,
                headers: NoCacheHeaders, // we are disabling browser caching, to make sure react-query will handle the caching.
            },
        );

        const allCampaigns = [
            ...userCampaigns,
            ...sharedCampaigns,
            ...(demoCampaigns || []).map((campaign) => ({
                ...campaign,
                isDemo: true,
            })),
        ].map(withAllModelsConfig);

        return allCampaigns;
    } catch (error) {
        Sentry.errorTracker({
            e: error,
            message: "Error fetching LLM Tracker campaigns",
            level: "error",
        });
        return [];
    }
};

const fetchCampaignById = async (id?: string): Promise<IAiTrackerCampaign | undefined> => {
    if (!id) {
        return;
    }

    try {
        const campaigns = await queryClient.fetchQuery<IAiTrackerCampaign[]>(campaignOptions.getAll());

        const campaign = campaigns.find((c) => c.id === id);
        if (campaign) {
            return campaign;
        }
        return;
    } catch (error) {
        Sentry.errorTracker({
            e: error,
            message: "Error fetching LLM Tracker campaign by id",
            level: "error",
            params: {
                extra: { id },
            },
        });
        return;
    }
};

export const campaignOptions: Record<string, (id?: string) => UseQueryOptions<any>> = {
    getAll: () => ({
        queryKey: campaignKeys.getAll,
        queryFn: fetchCampaigns,
        staleTime: TEN_MINUTES,
        gcTime: TEN_MINUTES,
        retry: 1,
    }),

    getById: (id?: string) => ({
        queryKey: campaignKeys.getById(id),
        queryFn: () => fetchCampaignById(id),
        refetchOnWindowFocus: false,
        refetchOnReconnect: false,
        staleTime: TEN_MINUTES,
        gcTime: TEN_MINUTES,
        retry: 1,
        enabled: !!id,
    }),
} as const;

export const campaignMutations = {
    create: createCampaign,
    createWithPrompts: createCampaignWithPrompts,
    update: updateCampaign,
    details: getDetails,
    delete: deleteCampaign,
    subscribeWeeklyReport,
} as const;
